'use client';

import { useState, useRef, useEffect } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ChevronLeft, ChevronRight, Building2 } from 'lucide-react';
import Image from 'next/image';
import { useLanguage } from '@/app/context/language-context';

// Mock data for partner developers
const partners = [
  {
    id: 1,
    name: 'Emaar',
    logo: 'https://images.x-space.ae/assets/images/Developer Logo Pic/emaar.webp',
    description:
      'Master developer of Burj Khalifa, Downtown Dubai, and leading lifestyle destinations.',
    website: 'https://www.emaar.com',
  },
  {
    id: 2,
    name: 'Sobha Realty',
    logo: 'https://images.x-space.ae/assets/images/Developer Logo Pic/sobha.webp',
    description:
      'Luxury real estate developer known for Sobha Hartland and premium waterfront communities.',
    website: 'https://sobharealty.com/',
  },
  {
    id: 3,
    name: 'Damac Properties',
    logo: 'https://images.x-space.ae/assets/images/Developer Logo Pic/damac.webp',
    description:
      'Developer of luxury residences and branded real estate such as DAMAC Hills and Akoya.',
    website: 'https://www.damacproperties.com/en/',
  },
  {
    id: 4,
    name: 'Binghatti',
    logo: 'https://images.x-space.ae/assets/images/Developer Logo Pic/binghatti-logo-dark.png',
    description:
      'Leading developer of iconic projects like Burj Khalifa and Dubai Mall.',
    website: 'https://www.binghatti.com/',
  },
  {
    id: 5,
    name: 'Heart Estate Properties',
    logo: 'https://images.x-space.ae/assets/images/Developer Logo Pic/thoe.png',
    description:
      'Luxury developer known for high-end residential and commercial properties.',
    website: 'https://heartestate.ae',
  },
  {
    id: 6,
    name: 'Nakheel',
    logo: 'https://images.x-space.ae/assets/images/Developer Logo Pic/Nakheel.png',
    description:
      'Developer of Palm Jumeirah and other iconic waterfront projects.',
    website: 'https://www.nakheel.com',
  },
  {
    id: 7,
    name: 'Dubai Properties',
    logo: 'https://images.x-space.ae/assets/images/Developer Logo Pic/dubai-properties-logo.png',
    description: 'Developer of mixed-use destinations across Dubai.',
    website: 'https://www.dp.ae/',
  },
  {
    id: 8,
    name: 'Meraas',
    logo: 'https://images.x-space.ae/assets/images/Developer Logo Pic/meraas.png',
    description:
      'Developer of lifestyle destinations like City Walk and La Mer.',
    website: 'https://meraas.com/en',
  },
  {
    id: 9,
    name: 'Tiger Properties',
    logo: 'https://images.x-space.ae/assets/images/Developer Logo Pic/tiger.png',
    description:
      'Developer of premium luxury properties with exceptional craftsmanship.',
    website: 'https://www.tigergroup.ae/',
  },
];

export default function PartnerDevelopersSection() {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(4);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const sliderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [direction, setDirection] = useState(1); // 1 for next, -1 for prev
  const [isAnimating, setIsAnimating] = useState(false);

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  // Card animation variants
  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.8,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        x: { type: 'spring', stiffness: 300, damping: 30 },
        opacity: { duration: 0.4 },
        scale: { duration: 0.4 },
      },
    },
    exit: (direction) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.8,
      transition: {
        x: { type: 'spring', stiffness: 300, damping: 30 },
        opacity: { duration: 0.4 },
        scale: { duration: 0.4 },
      },
    }),
  };

  // Update items per view based on screen size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      if (mobile) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else if (window.innerWidth < 1280) {
        setItemsPerView(3);
      } else {
        setItemsPerView(4);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Trigger animation when section comes into view
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  // Navigation functions
  // const nextSlide = () => {
  //   setCurrentIndex((prev) => {
  //     const nextIndex = prev + 1;
  //     return nextIndex >= partners.length - itemsPerView + 1 ? 0 : nextIndex;
  //   });
  // };

  // const prevSlide = () => {
  //   setCurrentIndex((prev) => {
  //     const prevIndex = prev - 1;
  //     return prevIndex < 0 ? partners.length - itemsPerView : prevIndex;
  //   });
  // };

  // Navigation functions
  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(1);

    const maxIndex = Math.max(0, partners.length - itemsPerView);
    const newIndex = currentIndex >= maxIndex ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);

    // Update scroll position for mobile
    if (sliderRef.current && isMobile) {
      const cardWidth = sliderRef.current.scrollWidth / partners.length;
      sliderRef.current.scrollTo({
        left: newIndex * cardWidth,
        behavior: 'smooth',
      });
    }

    setTimeout(() => setIsAnimating(false), 500);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(-1);

    const maxIndex = Math.max(0, partners.length - itemsPerView);
    const newIndex = currentIndex <= 0 ? maxIndex : currentIndex - 1;
    setCurrentIndex(newIndex);

    // Update scroll position for mobile
    if (sliderRef.current && isMobile) {
      const cardWidth = sliderRef.current.scrollWidth / partners.length;
      sliderRef.current.scrollTo({
        left: newIndex * cardWidth,
        behavior: 'smooth',
      });
    }

    setTimeout(() => setIsAnimating(false), 500);
  };

  // Mouse drag handlers for slider (mobile only)
  const handleMouseDown = (e) => {
    if (!sliderRef.current || !isMobile) return;
    setIsDragging(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
    sliderRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !sliderRef.current || !isMobile) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    sliderRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    if (!isMobile) return;
    setIsDragging(false);
    if (sliderRef.current) {
      sliderRef.current.style.cursor = 'grab';

      // Calculate which card is most visible and set currentIndex accordingly
      if (sliderRef.current.scrollLeft > 0) {
        const cardWidth = sliderRef.current.offsetWidth;
        const newIndex = Math.round(sliderRef.current.scrollLeft / cardWidth);
        setCurrentIndex(Math.min(newIndex, partners.length - itemsPerView));
      }
    }
  };

  const handleScroll = () => {
    if (!isMobile || !sliderRef.current) return;
    const cardWidth = sliderRef.current.offsetWidth;
    const newIndex = Math.round(sliderRef.current.scrollLeft / cardWidth);
    if (newIndex !== currentIndex) {
      setCurrentIndex(Math.min(newIndex, partners.length - itemsPerView));
    }
  };

  // Get visible items based on current index and items per view
  const getVisiblePartners = () => {
    return partners.slice(currentIndex, currentIndex + itemsPerView);
  };

  // Mouse drag handlers for slider
  // const handleMouseDown = (e) => {
  //   if (!sliderRef.current) return;
  //   setIsDragging(true);
  //   setStartX(e.pageX - sliderRef.current.offsetLeft);
  //   setScrollLeft(sliderRef.current.scrollLeft);
  // };

  // const handleMouseMove = (e) => {
  //   if (!isDragging || !sliderRef.current) return;
  //   e.preventDefault();
  //   const x = e.pageX - sliderRef.current.offsetLeft;
  //   const walk = (x - startX) * 2; // Scroll speed multiplier
  //   sliderRef.current.scrollLeft = scrollLeft - walk;
  // };

  // const handleMouseUp = () => {
  //   setIsDragging(false);
  // };

  return (
    <motion.section
      ref={ref}
      className='py-16 px-4 bg-gray-50 dark:bg-gray-800'
      variants={sectionVariants}
      initial='hidden'
      animate={controls}
    >
      <div className='max-w-7xl mx-auto'>
        {/* Section Header */}
        <div className='text-center mb-12'>
          <motion.div
            className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-primarycolor/10 mb-4'
            variants={titleVariants}
          >
            <Building2 className='w-8 h-8 text-primarycolor' />
          </motion.div>
          <motion.h2
            className='text-3xl font-bold text-gray-900 dark:text-white mb-4'
            variants={titleVariants}
          >
            {t('partnerDevelopers')}
          </motion.h2>
          <motion.p
            className='text-gray-600 dark:text-gray-400 max-w-2xl mx-auto'
            variants={titleVariants}
          >
            {t('partnerDevelopersDescription')}
          </motion.p>
        </div>

        {/* Slider Controls */}
        <div
          className={`flex justify-end mb-6 space-x-2 ${
            isMobile ? 'hidden' : 'block'
          }`}
        >
          <motion.button
            onClick={prevSlide}
            className='p-3 rounded-full bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors shadow-md'
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label='Previous slide'
          >
            <ChevronLeft className='w-5 h-5' />
          </motion.button>
          <motion.button
            onClick={nextSlide}
            className='p-3 rounded-full bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors shadow-md'
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label='Next slide'
          >
            <ChevronRight className='w-5 h-5' />
          </motion.button>
        </div>

        {/* Partners Slider */}
        <div
          className={`relative overflow-hidden ${
            isMobile ? 'cursor-grab active:cursor-grabbing' : ''
          }`}
          ref={sliderRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onScroll={handleScroll}
          style={{
            cursor:
              isDragging && isMobile
                ? 'grabbing'
                : isMobile
                  ? 'grab'
                  : 'default',
          }}
        >
          {isMobile ? (
            // Mobile view - horizontal scrolling
            <div
              className='flex overflow-x-auto snap-x snap-mandatory hide-scrollbar'
              style={{ scrollBehavior: 'smooth' }}
            >
              {partners.map((partner) => (
                <div
                  key={partner.id}
                  className='min-w-[85vw] sm:min-w-[320px] snap-center px-3 mb-8'
                >
                  <div className='bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform perspective-1000 h-full'>
                    <div className='relative h-40 flex items-center justify-center p-6 bg-gray-100 dark:bg-gray-800'>
                      <Image
                        src={partner.logo}
                        alt={partner.name}
                        width={200}
                        height={200}
                        sizes='(max-width: 768px) 100px, 150px'
                        className='object-contain h-20 w-auto'
                        unoptimized
                      />
                    </div>
                    <div className='p-6'>
                      <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
                        {partner.name}
                      </h3>
                      <p className='text-gray-600 dark:text-gray-400 mb-4'>
                        {partner.description}
                      </p>
                      <a
                        href={partner.website}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center text-primarycolor dark:text-secondarycolor hover:underline'
                      >
                        Visit Website
                        <svg
                          className='w-4 h-4 ml-1'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M14 5l7 7m0 0l-7 7m7-7H3'
                          />
                        </svg>
                      </a>
                    </div>

                    {/* 3D decorative elements */}
                    <div
                      className='absolute -right-4 -bottom-4 w-16 h-16 rounded-full bg-primarycolor/5'
                      style={{ transform: 'translateZ(20px)' }}
                    />
                    <div className='absolute -left-4 -top-4 w-12 h-12' />
                    <div
                      className='absolute -left-4 -top-4 w-12 h-12 rounded-full bg-secondarycolor/5'
                      style={{ transform: 'translateZ(30px)' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Desktop view - controlled by navigation
            <AnimatePresence initial={false} mode='wait' custom={direction}>
              <motion.div
                key={currentIndex}
                className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 my-8'
                custom={direction}
                initial='enter'
                animate='center'
                exit='exit'
                variants={{
                  enter: { opacity: 0, x: direction > 0 ? 300 : -300 },
                  center: { opacity: 1, x: 0 },
                  exit: { opacity: 0, x: direction > 0 ? -300 : 300 },
                }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
              >
                {getVisiblePartners().map((partner, index) => (
                  <motion.div
                    key={partner.id}
                    className='min-w-[280px] sm:min-w-[320px] md:min-w-[350px] lg:min-w-[300px] xl:min-w-[320px] px-3'
                    variants={cardVariants}
                    whileHover={{
                      y: -10,
                      transition: { duration: 0.3 },
                    }}
                  >
                    <div className='bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform perspective-1000 h-full'>
                      <div className='relative h-40 flex items-center justify-center p-6 bg-gray-100 dark:bg-white'>
                        <Image
                          src={partner.logo}
                          alt={partner.name}
                          width={200} // required by Next.js
                          height={200} // required by Next.js
                          priority={index === 0}
                          sizes='(max-width: 768px) 100px, 150px'
                          className='object-contain h-auto w-auto max-h-20 max-w-[200px]'
                          unoptimized
                          // loading='lazy'
                        />
                      </div>
                      <div className='p-6'>
                        <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
                          {partner.name}
                        </h3>
                        <p className='text-gray-600 dark:text-gray-400 mb-4'>
                          {partner.description}
                        </p>
                        <a
                          href={partner.website}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='inline-flex items-center text-primarycolor dark:text-secondarycolor hover:underline'
                        >
                          Visit Website
                          <svg
                            className='w-4 h-4 ml-1'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M14 5l7 7m0 0l-7 7m7-7H3'
                            />
                          </svg>
                        </a>
                      </div>

                      {/* 3D decorative elements */}
                      <div
                        className='absolute -right-4 -bottom-4 w-16 h-16 rounded-full bg-primarycolor/5'
                        style={{ transform: 'translateZ(20px)' }}
                      />
                      <div
                        className='absolute -left-4 -top-4 w-12 h-12 rounded-full bg-secondarycolor/5'
                        style={{ transform: 'translateZ(30px)' }}
                      />
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        {/* Mobile scroll indicator - Only visible on mobile */}
        <div
          className={`mb-4 text-center text-sm text-gray-500 dark:text-gray-400 ${
            isMobile ? 'block' : 'hidden'
          }`}
        >
          <span className='inline-flex items-center'>
            <ChevronLeft className='w-4 h-4 mr-1' />
            Swipe to explore
            <ChevronRight className='w-4 h-4 ml-1' />
          </span>
        </div>

        {/* Pagination Dots */}
        <div
          className={`flex justify-center mt-8 space-x-2 ${
            isMobile ? 'hidden' : 'block'
          }`}
        >
          {Array.from({
            length: Math.ceil(partners.length / itemsPerView),
          }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index * itemsPerView)}
              className={`w-3 h-3 rounded-full transition-colors ${
                Math.floor(currentIndex / itemsPerView) === index
                  ? 'bg-primarycolor'
                  : 'bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </motion.section>
  );
}
